import { cloneDeep } from '@apollo/client/utilities';
import { Done } from '@mui/icons-material';
import { Alert, Box, Button, Divider, FormControl, FormLabel, Link, Paper, TextField, Typography } from '@mui/material';
import { ECertificateStatus, EMicrosoftConnectionState } from '@sharedInterfaces/ICompany';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import setMicrosoftSettings from '@src/APIs/graphQl/Company/setMicrosoftSettings';
import uploadMicrosoftCertificate from '@src/APIs/graphQl/Company/uploadMicrosoftCertificate';
import type Company from '@src/Objects/Company';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

interface MicrosoftSettingsTabProps {
    company: Company;
    setCompany: (company: Company) => void;
    reload: () => void;
}

// Regex patterns for validation
const GUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const CERTIFICATE_THUMBPRINT_REGEX = /^[A-Fa-f0-9]{40}$/;

export function MicrosoftSettingsTab({ company, setCompany }: MicrosoftSettingsTabProps) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;
    const [tenantId, setTenantId] = useState<string>('');
    const [clientId, setClientId] = useState<string>('');
    const [certificateThumbprint, setCertificateThumbprint] = useState<string>('');
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const [changed, setChanged] = useState<boolean>(false);
    const [isValid, setIsValid] = useState<boolean>(false); // For overall validation
    const [fileContent, setFileContent] = useState<string | null>(null); // For storing PEM file content
    const [isValidPem, setIsValidPem] = useState<boolean | null>(null);
    const [uploadingCertificate, setUploadingCertificate] = useState<boolean>(false);
    const [certficateError, setCertficateError] = useState<string | null>(null);

    useEffect(() => {
        if (company.microsoftSettings?.tenantId) setTenantId(company.microsoftSettings.tenantId);
        if (company.microsoftSettings?.clientId) setClientId(company.microsoftSettings.clientId);
        if (company.microsoftSettings?.certificateThumbprint)
            setCertificateThumbprint(company.microsoftSettings.certificateThumbprint);
    }, [company]);

    // Validate inputs based on regex patterns
    useEffect(() => {
        const isTenantIdValid = GUID_REGEX.test(tenantId);
        const isClientIdValid = GUID_REGEX.test(clientId);
        const isThumbprintValid = CERTIFICATE_THUMBPRINT_REGEX.test(certificateThumbprint);

        setIsValid(isTenantIdValid && isClientIdValid && isThumbprintValid);
    }, [tenantId, clientId, certificateThumbprint]);

    const uploadCertificate = (certificate: string) => {
        setUploadingCertificate(true);
        uploadMicrosoftCertificate(certificate)
            .then(setCompany)
            .then(() => {
                setCertficateError(null);
            })
            .catch((err) => {
                console.log(err);
                const clone = cloneDeep(company);
                clone.microsoftSettings.certificateStatus = ECertificateStatus.Invalid;
                clone.microsoftSettings.connectionState = EMicrosoftConnectionState.Error;
                setCompany(clone);
                setCertficateError(err.message);
            })
            .finally(() => {
                setUploadingCertificate(false);
                setFileContent(null);
                setUploadedFile(null);
            });
    };
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            setUploadedFile(file);

            // Create a FileReader to read the file's content
            const reader = new FileReader();
            reader.onload = (e) => {
                const text = e.target?.result as string;
                setFileContent(text); // Set the content of the file
                const hasBegin = text.indexOf('-----BEGIN PRIVATE KEY-----');
                const hasEnd = text.indexOf('-----END PRIVATE KEY-----');
                if (hasBegin !== -1 && hasEnd !== -1 && hasBegin < hasEnd) {
                    setIsValidPem(true); // Valid PEM private key
                    uploadCertificate(text);
                } else {
                    setIsValidPem(false); // Invalid PEM private key
                }
            };

            reader.onerror = (e) => {
                console.error('Error reading file:', e);
            };

            reader.readAsText(file); // Read the file as text
        }
    };

    const handleSave = () => {
        setSaving(true);
        setMicrosoftSettings({ tenantId, clientId, certificateThumbprint })
            .then(setCompany)
            .then(() => setChanged(false))
            .catch((err) => setSaveError(err.toString()))
            .finally(() => setSaving(false));
    };

    return (
        <Paper sx={{ mt: 3, p: 3, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {langStrings.microsoft}
                </Typography>
                <Divider />

                <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" gutterBottom>
                        {langStrings.microsoftRegistrationDescription}
                    </Typography>

                    <Box component="ol" sx={{ pl: 2, '& li': { mb: 1 } }}>
                        <li>
                            <Typography variant="body1" component="span">
                                {langStrings.microsoftRegistrationStep1}
                            </Typography>
                            <Link
                                href={langStrings.microsoftRegistrationLink}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {langStrings.microsoftRegistrationLinkText}
                            </Link>
                        </li>
                        <li>
                            <Typography variant="body1">{langStrings.microsoftRegistrationStep2}</Typography>
                        </li>
                        <li>
                            <Typography variant="body1">{langStrings.microsoftRegistrationStep3}</Typography>
                        </li>
                        <li>
                            <Typography variant="body1">{langStrings.microsoftRegistrationStep4}</Typography>
                        </li>
                        <li>
                            <Typography variant="body1">{langStrings.microsoftRegistrationStep5}</Typography>
                            <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                                <li>
                                    <Typography variant="body1">
                                        <strong>{langStrings.microsoftRegistrationPermissionMailSend}</strong>
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body1">
                                        <strong>{langStrings.microsoftRegistrationPermissionUserReadAll}</strong>
                                    </Typography>
                                </li>
                            </Box>
                            <Typography variant="body1">{langStrings.microsoftRegistrationStep6}</Typography>
                        </li>
                        <li>
                            <Typography variant="body1">{langStrings.microsoftRegistrationStep7}</Typography>
                        </li>
                    </Box>
                </Box>

                <FormControl>
                    <FormLabel>{langStrings.microsoftTenantId}</FormLabel>
                    <TextField
                        value={tenantId}
                        onChange={(e) => {
                            setTenantId(e.target.value);
                            setChanged(true);
                        }}
                        size="small"
                        error={!GUID_REGEX.test(tenantId) && tenantId !== ''}
                        helperText={!GUID_REGEX.test(tenantId) && tenantId !== '' ? langStrings.invalidTenantId : ''}
                    />
                </FormControl>

                <FormControl>
                    <FormLabel>{langStrings.microsoftClientId}</FormLabel>
                    <TextField
                        value={clientId}
                        onChange={(e) => {
                            setClientId(e.target.value);
                            setChanged(true);
                        }}
                        size="small"
                        error={!GUID_REGEX.test(clientId) && clientId !== ''}
                        helperText={!GUID_REGEX.test(clientId) && clientId !== '' ? langStrings.invalidClientId : ''}
                    />
                </FormControl>

                <FormControl>
                    <FormLabel>{langStrings.microsoftCertificateThumbprint}</FormLabel>
                    <TextField
                        value={certificateThumbprint}
                        onChange={(e) => {
                            setCertificateThumbprint(e.target.value);
                            setChanged(true);
                        }}
                        size="small"
                        error={
                            !CERTIFICATE_THUMBPRINT_REGEX.test(certificateThumbprint) && certificateThumbprint !== ''
                        }
                        helperText={
                            !CERTIFICATE_THUMBPRINT_REGEX.test(certificateThumbprint) && certificateThumbprint !== ''
                                ? langStrings.invalidThumbprint
                                : ''
                        }
                    />
                </FormControl>

                {saveError && (
                    <Alert severity="error" onClose={() => setSaveError(null)}>
                        {saveError}
                    </Alert>
                )}

                {changed && (
                    <Button
                        variant="contained"
                        startIcon={<Done />}
                        disabled={saving || !isValid}
                        onClick={handleSave}
                        sx={{ maxWidth: 200 }}
                    >
                        {langStrings.save}
                    </Button>
                )}

                <FormControl>
                    <FormLabel>{langStrings.uploadCertificate}</FormLabel>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <input
                            type="file"
                            onChange={handleFileChange}
                            disabled={!isValid || changed || saving || uploadingCertificate}
                            accept=".pem"
                        />
                        {!isValidPem && fileContent && (
                            <Typography variant="caption" color="error">
                                {langStrings.invalidPemFile}
                            </Typography>
                        )}
                        {certficateError && (
                            <Typography variant="caption" color="error">
                                {certficateError}
                            </Typography>
                        )}
                    </Box>
                </FormControl>

                <FormControl>
                    <FormLabel>{langStrings.microsoftCertificateStatus}</FormLabel>
                    <Typography variant="body2">{company.microsoftSettings?.certificateStatus}</Typography>
                </FormControl>

                <FormControl>
                    <FormLabel>{langStrings.microsoftConnectionStatus}</FormLabel>
                    <Typography variant="body2">{company.microsoftSettings?.connectionState}</Typography>
                </FormControl>
            </Box>
        </Paper>
    );
}
